import React, { useCallback, useEffect, useState } from 'react';

import {

  Alert,

  Button,

  Space,

  Spin,

  Table,

  Tag,

  Typography,

} from 'antd';

import { ArrowLeftOutlined, MedicineBoxOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../assets/utils/lib';
import DashboardSection from '../../features/dashboard/components/DashboardSection';

import { normalizeCheckinTypeListRow } from './checkinTypeNormalize';



const { Title, Text } = Typography;



const getServerError = (e, fallback) =>

  (e?.response?.data?.message != null && e.response.data.message !== '')

    ? e.response.data.message

    : (e?.message || fallback);



const formatSchedule = (row) => {

  const from = row?.active_from;

  const to = row?.active_to;

  if (!from && !to) return 'Always';

  if (from && to) return `${from} → ${to}`;

  if (from) return `From ${from}`;

  return `Until ${to}`;

};



const formatPolicySummary = (summary) => {

  if (!summary || typeof summary !== 'object') return '—';

  const parts = [];

  if (summary.facility_type_count != null) parts.push(`${summary.facility_type_count} kinds`);

  if (summary.facility_count != null) parts.push(`${summary.facility_count} sites`);

  if (summary.category_count != null) parts.push(`${summary.category_count} cat.`);

  if (summary.tariff_link_count != null) parts.push(`${summary.tariff_link_count} tariff`);

  if (summary.item_rule_count != null) parts.push(`${summary.item_rule_count} items`);

  return parts.length ? parts.join(' · ') : '—';

};



const AdminCheckinTypesList = ({ embedded = false, onOpenType, onCreateType }) => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [types, setTypes] = useState([]);



  const fetchTypes = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const parsed = lib.parseApiEnvelope(await lib.listAdminCheckinTypes());

      if (!parsed.ok) {

        setError(parsed.message || 'Unable to load encounter types');

        setTypes([]);

        return;

      }

      const payload = parsed.data ?? parsed.body;

      const list = Array.isArray(payload)

        ? payload

        : Array.isArray(payload?.checkin_types)

          ? payload.checkin_types

          : [];

      setTypes(list.map(normalizeCheckinTypeListRow));

    } catch (e) {

      setError(getServerError(e, 'Network error while loading encounter types'));

      setTypes([]);

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    fetchTypes();

  }, [fetchTypes]);



  const columns = [

    {

      title: 'Name',

      dataIndex: 'checkin_type_name',

      key: 'checkin_type_name',

      render: (name, row) => (

        <Space direction="vertical" size={0}>

          <Text strong>{name || '—'}</Text>

          {row?.checkin_types_description && (

            <Text type="secondary" style={{ fontSize: 12 }}>{row.checkin_types_description}</Text>

          )}

        </Space>

      ),

    },

    {

      title: 'Enabled',

      key: 'status',

      width: 100,

      render: (_, row) => {

        const active = Number(row.checkin_types_status) === 1;

        return (

          <Tag color={active ? 'green' : 'default'}>

            {row.status_label || (active ? 'Active' : 'Disabled')}

          </Tag>

        );

      },

    },

    {

      title: 'Plans',

      key: 'linked_plan_count',

      width: 72,

      render: (_, row) => (

        <Text type="secondary">{row.linked_plan_count != null ? row.linked_plan_count : '—'}</Text>

      ),

    },

    {

      title: 'Eligibility',

      key: 'entitlement',

      width: 200,

      render: (_, row) => {

        if (row.entitlement_mode === 'category_required') {

          const name = row.limit_pool_category_name;

          return (

            <Tag color="blue">

              {row.entitlement_mode_label || 'Plan benefit'}

              {name ? `: ${name}` : ` (#${row.plans_categories_id ?? '?'})`}

            </Tag>

          );

        }

        return <Tag>{row.entitlement_mode_label || 'Generic'}</Tag>;

      },

    },

    {

      title: 'Rules',

      key: 'policy_summary',

      width: 180,

      render: (_, row) => (

        <Text type="secondary" style={{ fontSize: 12 }}>

          {formatPolicySummary(row.policy_summary)}

        </Text>

      ),

    },

    {

      title: 'Order',

      dataIndex: 'display_order',

      key: 'display_order',

      width: 70,

    },

    {

      title: 'Schedule',

      key: 'schedule',

      width: 200,

      render: (_, row) => <Text type="secondary" style={{ fontSize: 12 }}>{formatSchedule(row)}</Text>,

    },

    {

      title: '',

      key: 'actions',

      width: 90,

      render: (_, row) => (

        <Button type="link" onClick={() => (onOpenType ? onOpenType(String(row.checkin_type_id)) : navigate(`/plans/encounter-types/${row.checkin_type_id}`))}>

          Edit

        </Button>

      ),

    },

  ];



  const headerActions = (
    <>
      <Button size="small" icon={<ReloadOutlined />} onClick={fetchTypes} disabled={loading}>
        Refresh
      </Button>
      <Button
        size="small"
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => (onCreateType ? onCreateType() : navigate('/plans/encounter-types/new'))}
      >
        New type
      </Button>
    </>
  );

  const body = (
    <>
      <Alert
        type="info"
        showIcon
        message="Each row is a visit type facilities see at check-in"
        description={(
          <span>
            Click <strong>Edit</strong> to set up in order:
            {' '}<strong>Basics</strong> → <strong>Who</strong> → <strong>Kinds</strong> → <strong>Sites</strong> → <strong>Prices</strong> → <strong>Services</strong> → <strong>Documents</strong>.
            {' '}Plan spending caps are configured separately under <strong>Plans → Limits</strong>.
          </span>
        )}
      />
      {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}
      <Spin spinning={loading}>
        <Table
          rowKey="checkin_type_id"
          columns={columns}
          dataSource={types}
          pagination={false}
          locale={{ emptyText: loading ? 'Loading…' : 'No encounter types yet' }}
        />
      </Spin>
    </>
  );

  if (embedded) {
    return (
      <DashboardSection
        id="plans-encounter-types"
        icon={<MedicineBoxOutlined />}
        title="Encounter types"
        subtitle={`${types.length} type${types.length === 1 ? '' : 's'}`}
        collapsible={false}
        variant="content"
        action={headerActions}
      >
        {body}
      </DashboardSection>
    );
  }

  return (

    <div style={{ padding: embedded ? 0 : '16px 24px', maxWidth: embedded ? '100%' : 1200 }}>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>

        {!embedded ? (
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>

          <Space>

            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/plans?tab=encounter-types')}>

                Plans

              </Button>

            <Title level={3} style={{ margin: 0 }}>Encounter Types</Title>

          </Space>

          <Space>{headerActions}</Space>

        </Space>
        ) : null}

        {body}

      </Space>

    </div>

  );

};



export default AdminCheckinTypesList;


